import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  FaProjectDiagram, 
  FaMoneyBillWave, 
  FaFileInvoiceDollar, 
  FaChartLine, 
  FaClock,
  FaArrowUp,
  FaEye,
  FaCalendarAlt,
  FaBell,
  FaTasks,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowRight,
  FaPlus,
  FaFilter,
  FaDownload,
  FaChartBar
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

import apiService from '../../services/api';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title);

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const DashboardContainer = styled.div`
  padding: 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: calc(100vh - 80px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(20, 19, 64, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const DashboardContent = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 2rem;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const PageHeader = styled.div`
  grid-column: span 12;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &::after {
    content: '';
    width: 4px;
    height: 40px;
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: white;
  color: #141340;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  &:hover {
    background: #f8f9fa;
    border-color: #F68B21;
    color: #F68B21;
    box-shadow: 0 4px 15px rgba(246, 139, 33, 0.2);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  grid-column: span 12;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ color?: string; delay?: number }>`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out ${props => props.delay || 0}s both;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, ${props => props.color || '#F68B21'} 0%, ${props => props.color || '#e67c1a'} 100%);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    
    .stat-icon {
      animation: ${pulse} 0.6s ease-in-out;
    }
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-shrink: 0;
`;

const StatIcon = styled.div<{ bgColor?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${props => props.bgColor || '#F68B21'} 0%, ${props => props.bgColor || '#e67c1a'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 8px 24px ${props => props.bgColor || '#F68B21'}40;
  transition: all 0.3s ease;
  flex-shrink: 0;
`;

const StatTrend = styled.div<{ positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${props => props.positive ? '#28a745' : '#dc3545'};
  font-weight: 600;
  background: ${props => props.positive ? '#d4edda' : '#f8d7da'};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  border: 1px solid ${props => props.positive ? '#c3e6cb' : '#f5c6cb'};
  white-space: nowrap;
  flex-shrink: 0;
`;

const StatValue = styled.div`
  font-size: clamp(1.5rem, 4vw, 2.25rem);
  font-weight: 800;
  color: #141340;
  margin-bottom: 0.5rem;
  line-height: 1.1;
  word-break: break-all;
  overflow-wrap: break-word;
  flex-grow: 1;
  display: flex;
  align-items: center;
  min-height: 2.5rem;
  
  @media (max-width: 768px) {
    font-size: clamp(1.25rem, 5vw, 1.75rem);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1rem, 6vw, 1.5rem);
  }
`;

const StatLabel = styled.div`
  font-size: 0.95rem;
  color: #6c757d;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
`;

const MainSection = styled.div`
  grid-column: span 8;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-column: span 12;
  }
`;

const SideSection = styled.div`
  grid-column: span 4;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-column: span 12;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  transition: all 0.3s ease;
  animation: ${slideIn} 0.6s ease-out;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f8f9fa;
  
  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0;
  }
`;

const ViewAllLink = styled.button`
  color: #F68B21;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #fff3e0;
    text-decoration: none;
    transform: translateX(4px);
  }
`;

const ProjectStatusSection = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  max-width: 240px;
  max-height: 240px;
  margin: 0 auto;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    border-radius: 50%;
    opacity: 0.1;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    max-width: 200px;
    max-height: 200px;
  }
`;

const StatusList = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StatusItem = styled.div<{ color: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border-left: 4px solid ${props => props.color};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
  
  .status-name {
    font-weight: 600;
    color: #141340;
    font-size: 0.95rem;
  }
  
  .status-count {
    font-weight: 700;
    color: ${props => props.color};
    font-size: 1.1rem;
  }
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TaskItem = styled.div<{ $priority: 'high' | 'medium' | 'low' }>`
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border-left: 4px solid ${props => 
    props.$priority === 'high' ? '#dc3545' : 
    props.$priority === 'medium' ? '#ffc107' : '#28a745'
  };
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
  
  .task-title {
    font-weight: 600;
    color: #141340;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
  }
  
  .task-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
  }
  
  .task-priority {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    background: ${props => 
      props.$priority === 'high' ? '#f8d7da' : 
      props.$priority === 'medium' ? '#fff3cd' : '#d4edda'
    };
    color: ${props => 
      props.$priority === 'high' ? '#721c24' : 
      props.$priority === 'medium' ? '#856404' : '#155724'
    };
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityItem = styled.div`
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border-left: 4px solid ${props => props.color || '#F68B21'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
  
  .activity-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
  }
  
  .activity-title {
    font-weight: 600;
    color: #141340;
    font-size: 0.95rem;
  }
  
  .activity-meta {
    font-size: 0.85rem;
    color: #6c757d;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
  
  .activity-description {
    font-size: 0.9rem;
    color: #495057;
    line-height: 1.5;
  }
`;

const StatusBadge = styled.span<{ $status?: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.$status) {
      case 'Completed':
      case 'Approved':
        return 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)';
      case 'In-Progress':
      case 'Under Review':
        return 'linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)';
      case 'Draft':
        return 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)';
      default:
        return 'linear-gradient(135deg, #e2e3e5 0%, #d6d8db 100%)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'Completed':
      case 'Approved':
        return '#155724';
      case 'In-Progress':
      case 'Under Review':
        return '#0c5460';
      case 'Draft':
        return '#856404';
      default:
        return '#383d41';
    }
  }};
`;

const NotificationItem = styled.div<{ $read?: boolean }>`
  padding: 1rem;
  border-radius: 12px;
  background: ${props => props.$read ? 
    'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : 
    'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)'
  };
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  border-left: 4px solid ${props => props.$read ? '#dee2e6' : '#ffc107'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .notification-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
  }
  
  .notification-title {
    font-weight: 600;
    color: #141340;
    font-size: 0.95rem;
  }
  
  .notification-time {
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: 500;
  }
  
  .notification-message {
    font-size: 0.9rem;
    color: #495057;
    line-height: 1.5;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.2rem;
  color: #6c757d;
  flex-direction: column;
  gap: 1rem;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #F68B21;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  
  .empty-icon {
    font-size: 3rem;
    color: #dee2e6;
    margin-bottom: 1rem;
  }
  
  .empty-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
  }
  
  .empty-description {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

// Generate real tasks based on user role and data
const generateUserTasks = (user: any, recentProjects: any[], recentFundRequests: any[], pendingApprovals: any[]) => {
  const tasks: Array<{
    id: string;
    title: string;
    dueDate: Date;
    priority: 'high' | 'medium' | 'low';
    project: string;
  }> = [];
  
  if (!user) return tasks;
  
  // Tasks based on user role
  if (user.role === 'MD' || user.role === 'TD') {
    // High priority tasks for MD/TD
    if (pendingApprovals.length > 0) {
      tasks.push({
        id: 'task-1',
        title: `Review ${pendingApprovals.length} pending approvals`,
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
        priority: 'high' as const,
        project: 'Administrative'
      });
    }
    
    if (recentProjects.length > 0) {
      tasks.push({
        id: 'task-2',
        title: 'Review project status updates',
        dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days
        priority: 'medium' as const,
        project: 'Project Management'
      });
    }
    
    tasks.push({
      id: 'task-3',
      title: 'Monthly executive summary',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week
      priority: 'low' as const,
      project: 'Administrative'
    });
  } else if (user.role === 'Project Manager') {
    // Tasks for Project Managers
    const assignedProjects = recentProjects.filter((project: any) => 
      project.assignedStaff?.some((staff: any) => staff.userId === user.id)
    );
    
    if (assignedProjects.length > 0) {
      tasks.push({
        id: 'task-1',
        title: `Update ${assignedProjects.length} project progress`,
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
        priority: 'high' as const,
        project: 'Project Management'
      });
    }
    
    if (recentFundRequests.length > 0) {
      tasks.push({
        id: 'task-2',
        title: 'Review fund request status',
        dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
        priority: 'medium' as const,
        project: 'Financial Management'
      });
    }
  } else if (user.role === 'Accountant') {
    // Tasks for Accountants
    if (recentFundRequests.length > 0) {
      tasks.push({
        id: 'task-1',
        title: `Process ${recentFundRequests.length} fund requests`,
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
        priority: 'high' as const,
        project: 'Financial Management'
      });
    }
    
    tasks.push({
      id: 'task-2',
      title: 'Monthly financial reconciliation',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      priority: 'medium' as const,
      project: 'Administrative'
    });
  } else {
    // Default tasks for other roles
    tasks.push({
      id: 'task-1',
      title: 'Review assigned tasks',
      dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
      priority: 'medium' as const,
      project: 'General'
    });
  }
  
  return tasks;
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({
    totalProjects: 0,
    totalFundRequests: 0,
    totalUsers: 0,
    totalExpenses: 0,
    inProgressProjects: 0,
    completedProjects: 0,
    draftProjects: 0,
    pendingFundRequests: 0,
    approvedFundRequests: 0,
    rejectedFundRequests: 0,
    monthlySpending: 0,
    budgetUtilization: 0,
    currency: 'TZS'
  });
  const [recentProjects, setRecentProjects] = useState<any[]>([]);
  const [recentFundRequests, setRecentFundRequests] = useState<any[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [userNotifications, setUserNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load dashboard statistics
        const statsResponse = await apiService.getDashboardStats();
        setDashboardStats(statsResponse.data);
        
        // Load recent projects
        const projectsResponse = await apiService.getRecentProjects(3);
        setRecentProjects(projectsResponse.data);
        
        // Load recent fund requests
        const requestsResponse = await apiService.getRecentFundRequests(3);
        setRecentFundRequests(requestsResponse.data);
        
        // Load pending approvals
        const approvalsResponse = await apiService.getPendingApprovals();
        setPendingApprovals(approvalsResponse.data);
        
        // Load notifications from real API
        const notificationsResponse = await apiService.getNotifications({ limit: 5 });
        setUserNotifications(notificationsResponse.data);
        setUnreadCount(notificationsResponse.unreadCount || 0);
        
      } catch (error: any) {
        console.error('Error loading dashboard data:', error);
        setError('Failed to load dashboard data. Using mock data as fallback.');
        
        // Fallback to empty arrays for notifications
        setUserNotifications([]);
        setUnreadCount(0);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadDashboardData();
    }
  }, [user]);
  
  // Get projects by status for the status summary
  // For Project Manager, filter by assigned projects
  const getFilteredProjectsByStatus = (status: string) => {
    if (user?.role === 'Project Manager') {
      return recentProjects.filter((project: any) => 
        project.assignedStaff?.some((staff: any) => staff.userId === user.id) && 
        project.status === status
      );
    }
    return recentProjects.filter((project: any) => project.status === status);
  };
  
  const inProgressProjects = getFilteredProjectsByStatus('In-Progress');
  const draftProjects = getFilteredProjectsByStatus('Draft');
  const completedProjects = getFilteredProjectsByStatus('Completed');
  
  // Get fund requests by status - TODO: Replace with actual API call
  const pendingRequests = []; // Placeholder until API is ready
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  const formatLargeNumber = (num: number | undefined): string => {
    if (num === undefined || num === null) {
      return '0';
    }
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  const formatCurrencyForCard = (amount: number): string => {
    const currencySymbol = 'TZS ';
    if (amount >= 1000000000) {
      return currencySymbol + (amount / 1000000000).toFixed(1) + 'B';
    }
    if (amount >= 1000000) {
      return currencySymbol + (amount / 1000000).toFixed(1) + 'M';
    }
    if (amount >= 1000) {
      return currencySymbol + (amount / 1000).toFixed(1) + 'K';
    }
    return currencySymbol + amount.toLocaleString();
  };
  
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };
  
  if (isLoading) {
    return (
      <DashboardContainer>
        <LoadingSpinner>
          <div className="spinner"></div>
          <div>Loading dashboard...</div>
        </LoadingSpinner>
      </DashboardContainer>
    );
  }
  
  return (
    <DashboardContainer>
      <DashboardContent>
        <PageHeader>
          <PageTitle>
            <IconWrapper icon={FaChartLine} />
            Dashboard
          </PageTitle>
          <QuickActions>
            <ActionButton onClick={() => navigate('/reports')}>
              <IconWrapper icon={FaChartBar} />
              View Reports
            </ActionButton>
            <SecondaryButton onClick={() => navigate('/projects')}>
              <IconWrapper icon={FaProjectDiagram} />
              Manage Projects
            </SecondaryButton>
            <SecondaryButton onClick={() => window.print()}>
              <IconWrapper icon={FaDownload} />
              Export Data
            </SecondaryButton>
          </QuickActions>
        </PageHeader>
      
        <StatsGrid>
          <StatCard color="#17a2b8" delay={0.1}>
            <StatHeader>
              <StatIcon bgColor="#17a2b8" className="stat-icon">
                <IconWrapper icon={FaProjectDiagram} />
              </StatIcon>
              <StatTrend positive>
                <IconWrapper icon={FaArrowUp} />
                12%
              </StatTrend>
            </StatHeader>
            <StatLabel>Total Projects</StatLabel>
            <StatValue>{formatLargeNumber(dashboardStats?.totalProjects || 0)}</StatValue>
          </StatCard>

          <StatCard color="#ffc107" delay={0.2}>
            <StatHeader>
              <StatIcon bgColor="#ffc107" className="stat-icon">
                <IconWrapper icon={FaMoneyBillWave} />
              </StatIcon>
              <StatTrend positive>
                <IconWrapper icon={FaArrowUp} />
                8%
              </StatTrend>
            </StatHeader>
            <StatLabel>Pending Requests</StatLabel>
            <StatValue>{formatLargeNumber(dashboardStats?.pendingFundRequests || 0)}</StatValue>
          </StatCard>
          
          <StatCard color="#28a745" delay={0.3}>
            <StatHeader>
              <StatIcon bgColor="#28a745" className="stat-icon">
                <IconWrapper icon={FaFileInvoiceDollar} />
              </StatIcon>
              <StatTrend positive>
                <IconWrapper icon={FaArrowUp} />
                15%
              </StatTrend>
            </StatHeader>
            <StatValue>{formatCurrencyForCard(dashboardStats?.monthlySpending || 0)}</StatValue>
            <StatLabel>Monthly Spending</StatLabel>
          </StatCard>
          
          <StatCard color="#F68B21" delay={0.4}>
            <StatHeader>
              <StatIcon bgColor="#F68B21" className="stat-icon">
                <IconWrapper icon={FaChartLine} />
              </StatIcon>
              <StatTrend positive>
                <IconWrapper icon={FaArrowUp} />
                3%
              </StatTrend>
            </StatHeader>
            <StatValue>{dashboardStats?.budgetUtilization || 0}%</StatValue>
            <StatLabel>Budget Utilization</StatLabel>
          </StatCard>
        </StatsGrid>
      
        <MainSection>
          <Card>
            <CardHeader>
              <h3>
                <IconWrapper icon={FaProjectDiagram} />
                Project Status Overview
              </h3>
              <ViewAllLink onClick={() => navigate('/projects')}>
                View All <IconWrapper icon={FaArrowRight} />
              </ViewAllLink>
            </CardHeader>
            <ProjectStatusSection>
              <ChartContainer>
                <Doughnut 
                  data={{
                    labels: ['In Progress', 'Completed', 'Draft', 'On Hold'],
                    datasets: [{
                      data: [
                        inProgressProjects?.length || 0,
                        completedProjects?.length || 0,
                        draftProjects?.length || 0,
                        recentProjects?.filter((p: any) => p?.status === 'On-Hold')?.length || 0
                      ],
                      backgroundColor: ['#17a2b8', '#28a745', '#adb5bd', '#ffc107'],
                      borderWidth: 0
                    }]
                  }} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </ChartContainer>
              <StatusList>
                <StatusItem color="#17a2b8">
                  <span className="status-name">In Progress</span>
                  <span className="status-count">{inProgressProjects?.length || 0} projects</span>
                </StatusItem>
                <StatusItem color="#28a745">
                  <span className="status-name">Completed</span>
                  <span className="status-count">{completedProjects?.length || 0} projects</span>
                </StatusItem>
                <StatusItem color="#adb5bd">
                  <span className="status-name">Draft</span>
                  <span className="status-count">{draftProjects?.length || 0} projects</span>
                </StatusItem>
                <StatusItem color="#ffc107">
                  <span className="status-name">On Hold</span>
                  <span className="status-count">{recentProjects?.filter((p: any) => p?.status === 'On-Hold')?.length || 0} project</span>
                </StatusItem>
              </StatusList>
            </ProjectStatusSection>
          </Card>
        
          <Card>
            <CardHeader>
              <h3>
                <IconWrapper icon={FaProjectDiagram} />
                Recent Projects
              </h3>
              <ViewAllLink onClick={() => navigate('/projects')}>
                View All <IconWrapper icon={FaArrowRight} />
              </ViewAllLink>
            </CardHeader>
            {recentProjects.length > 0 ? (
              <ActivityList>
                {recentProjects.map((project: any) => (
                  <ActivityItem key={project?._id || project?.id || Math.random()} color={
                    project?.status === 'Completed' ? '#28a745' :
                    project?.status === 'In-Progress' ? '#17a2b8' :
                    project?.status === 'Draft' ? '#ffc107' : '#6c757d'
                  }>
                    <div className="activity-header">
                      <div className="activity-title">{project?.name || 'Unknown Project'}</div>
                      <StatusBadge $status={project?.status || 'Unknown'}>{project?.status || 'Unknown'}</StatusBadge>
                    </div>
                    <div className="activity-description">
                      <strong>Budget:</strong> {formatCurrency(project?.budget?.total || 0)} • 
                      <strong> Location:</strong> {project?.location || 'Unknown'}
                    </div>
                    <div className="activity-meta">
                      <IconWrapper icon={FaCalendarAlt} />
                      Updated {formatDate(new Date(project?.updatedAt || new Date()))}
                    </div>
                  </ActivityItem>
                ))}
              </ActivityList>
            ) : (
              <EmptyState>
                <div className="empty-icon">
                  <IconWrapper icon={FaProjectDiagram} />
                </div>
                <div className="empty-title">No Recent Projects</div>
                <div className="empty-description">Start by creating your first project to see it here.</div>
              </EmptyState>
            )}
          </Card>
        
          <Card>
            <CardHeader>
              <h3>
                <IconWrapper icon={FaMoneyBillWave} />
                Recent Fund Requests
              </h3>
              <ViewAllLink onClick={() => navigate('/funds')}>
                View All <IconWrapper icon={FaArrowRight} />
              </ViewAllLink>
            </CardHeader>
            {recentFundRequests.length > 0 ? (
              <ActivityList>
                {recentFundRequests.map((request: any) => (
                  <ActivityItem key={request?._id || request?.id || Math.random()} color={
                    request?.status === 'Approved' ? '#28a745' :
                    request?.status === 'Under Review' ? '#ffc107' :
                    request?.status === 'Rejected' ? '#dc3545' : '#6c757d'
                  }>
                    <div className="activity-header">
                      <div className="activity-title">{request?._id || request?.id || 'Unknown Request'}</div>
                      <StatusBadge $status={request?.status || 'Unknown'}>{request?.status || 'Unknown'}</StatusBadge>
                    </div>
                    <div className="activity-description">
                      <strong>Project:</strong> {request?.projectId?.name || request?.projectName || 'Unknown Project'} • 
                      <strong> Amount:</strong> {formatCurrency(request?.amount || 0)} • 
                      <strong> Requester:</strong> {request?.requesterId?.name || request?.requester || 'Unknown'}
                    </div>
                    <div className="activity-meta">
                      <IconWrapper icon={FaCalendarAlt} />
                      {formatDate(new Date(request?.updatedAt || new Date()))}
                    </div>
                  </ActivityItem>
                ))}
              </ActivityList>
            ) : (
              <EmptyState>
                <div className="empty-icon">
                  <IconWrapper icon={FaMoneyBillWave} />
                </div>
                <div className="empty-title">No Recent Fund Requests</div>
                <div className="empty-description">Fund requests will appear here once submitted.</div>
              </EmptyState>
            )}
          </Card>
        </MainSection>
        
        <SideSection>
          {unreadCount > 0 && (
            <Card>
              <CardHeader>
                <h3>
                  <IconWrapper icon={FaBell} />
                  Notifications
                  <span style={{ 
                    background: '#dc3545', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '20px', 
                    height: '20px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '0.75rem',
                    marginLeft: '0.5rem'
                  }}>
                    {unreadCount}
                  </span>
                </h3>
                <ViewAllLink onClick={() => navigate('/notifications')}>
                  View All <IconWrapper icon={FaArrowRight} />
                </ViewAllLink>
              </CardHeader>
              {userNotifications.slice(0, 3).map((notification) => (
                <NotificationItem key={notification._id || notification.id} $read={notification.read}>
                  <div className="notification-header">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-time">{getTimeAgo(new Date(notification.createdAt))}</div>
                  </div>
                  <div className="notification-message">{notification.message}</div>
                </NotificationItem>
              ))}
            </Card>
          )}
          
          <Card>
            <CardHeader>
              <h3>
                <IconWrapper icon={FaTasks} />
                Your Tasks
              </h3>
              <ViewAllLink onClick={() => {
                // Navigate based on user role
                if (user?.role === 'MD' || user?.role === 'TD') {
                  navigate('/reports');
                } else if (user?.role === 'Project Manager') {
                  navigate('/projects');
                } else if (user?.role === 'Accountant') {
                  navigate('/funds');
                } else {
                  navigate('/projects');
                }
              }}>
                View All <IconWrapper icon={FaArrowRight} />
              </ViewAllLink>
            </CardHeader>
            <TaskList>
              {generateUserTasks(user, recentProjects, recentFundRequests, pendingApprovals).map((task: any) => (
                <TaskItem key={task.id} $priority={task.priority}>
                  <div className="task-title">{task.title}</div>
                  <div className="task-meta">
                    <span>{task.project}</span>
                    <span className="task-priority">
                      {task.priority === 'high' && <IconWrapper icon={FaExclamationTriangle} />}
                      {task.priority === 'medium' && <IconWrapper icon={FaExclamationTriangle} />}
                      {task.priority === 'low' && <IconWrapper icon={FaCheckCircle} />}
                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                    </span>
                  </div>
                  <div className="activity-meta">
                    <IconWrapper icon={FaCalendarAlt} />
                    Due: {formatDate(task.dueDate)}
                  </div>
                </TaskItem>
              ))}
            </TaskList>
          </Card>
        
          <Card>
            <CardHeader>
              <h3>
                <IconWrapper icon={FaMoneyBillWave} />
                Pending Approvals
              </h3>
            </CardHeader>
            {pendingApprovals.length > 0 ? (
              <ActivityList>
                {pendingApprovals.slice(0, 3).map((item: any) => (
                  <ActivityItem key={item.item?._id || item.item?.id || Math.random()} color="#ffc107">
                    <div className="activity-header">
                      <div className="activity-title">
                        {item.type === 'fund_request' ? 
                          (item.item?._id || item.item?.id || 'Unknown') : 
                          (item.item?.purpose || 'Unknown')
                        }
                      </div>
                      <StatusBadge $status={item.item?.status || 'Unknown'}>{item.item?.status || 'Unknown'}</StatusBadge>
                    </div>
                    <div className="activity-description">
                      {item.type === 'fund_request' ? (
                        <>
                          <strong>Amount:</strong> {formatCurrency(item.item?.amount || 0)} • 
                          <strong> Requester:</strong> {item.item?.requesterId?.name || item.item?.requester || 'Unknown'}
                        </>
                      ) : (
                        <>
                          <strong>Amount:</strong> {formatCurrency(item.item?.amount || 0)} • 
                          <strong> Requester:</strong> {item.item?.requester?.name || item.item?.requester || 'Unknown'}
                        </>
                      )}
                    </div>
                    <div className="activity-meta">
                      <IconWrapper icon={FaCalendarAlt} />
                      Submitted: {formatDate(new Date(item.item?.createdAt || new Date()))}
                    </div>
                  </ActivityItem>
                ))}
              </ActivityList>
            ) : (
              <EmptyState>
                <div className="empty-icon">
                  <IconWrapper icon={FaCheckCircle} />
                </div>
                <div className="empty-title">No Pending Approvals</div>
                <div className="empty-description">All requests have been processed.</div>
              </EmptyState>
            )}
          </Card>
        </SideSection>
      </DashboardContent>
      
      <div style={{ 
        textAlign: 'right', 
        padding: '2rem 1rem 1rem', 
        color: '#6c757d', 
        fontSize: '0.8rem',
        position: 'relative',
        zIndex: 1
      }}>
        Developed by Sandfish
      </div>
    </DashboardContainer>
  );
};

export default Dashboard; 